<template>
    <div class="layout layout-center">
        <canvas id="game" width="1024" height="600" :class="{ inactive: state != 'running' }"></canvas>

        <div v-if="state == 'intro'" class="hud">
            <div class="stage">
                <Picture :src="'res/gfx/' + player1 + '_preview.png'" class="player player1"></Picture>
                <h2 class="mx-1">VS</h2>
                <Picture :src="'res/gfx/' + player2 + '_preview.png'" class="player player2"></Picture>
            </div>
            <h2>{{ introTexts.gameType }}</h2>
            <div class="my-4">{{ introTexts.gameTypeInfo }}</div>
            <a @click="start" class="btn">Let's go!</a>
        </div>

        <div v-if="state == 'finished'" class="hud">
            <div class="stage">
                <Picture src="res/gfx/player1_preview.png" class="player player1"></Picture>
            </div>
            <h2>{{ finishTexts.title }}</h2>
            <div class="my-4">{{ finishTexts.text }}</div>
            <div class="stage">
                <a href="javascript:" class="mx-2" @click="reset"><Picture src="res/gfx/yes.png"></Picture></a>
                <a href="javascript:" class="mx-2" @click="quit"><Picture src="res/gfx/no.png"></Picture></a>
            </div>
        </div>

        <div v-if="state == 'paused'" class="hud">
            <h1>Paused</h1>
            <ul class="actions">
                <li><a @click="resume" href="javascript:">Resume</a></li>
                <li><a @click="reset" href="javascript:">Restart</a></li>
                <li><a @click="quit" href="javascript:">Quit</a></li>
            </ul>
        </div>

        <a v-if="state == 'running'" @click="pause" id="gamemenu" class="inactive" href="javascript:"><Picture src="res/gfx/nav_settings.png" width="50"></Picture></a>

        <div v-if="state == 'running'" id="gamecontrols">
            <div id="gamecontrols-move" class="ctrl"></div>
            <div id="gamecontrols-action" class="ctrl"></div>
        </div>
    </div>
</template>

<script>
// @ts-check
import R from "../strings";
import { randa } from "../util";
import { Engine, Component } from "../engine";
import { GameLogic } from "../game/logic";
import { GameState } from "../game/model";
import { GameInput } from "../game/input";
import { GameSound } from "../game/sound";
import { GamePhysics } from "../game/physics";
import { GameRenderer } from "../game/renderer";
import { GameVibrator } from "../game/vibrator";
import { GameRendererHUD } from "../game/rendererHUD";
import { EVENT_GAME_PAUSE, EVENT_GAME_RESUME, EVENT_INGAME_KICKOFF, EVENT_GAME_RESET, EVENT_ENGINE_POST_START, EVENT_INGAME_FINISHED, GAMETYPE_BEST_OF, GAMETYPE_TIME, GAMETYPE_BOMB, PLAYER_1 } from "../global";

// Constants
const INTRO_TEXTS = {
    [GAMETYPE_BEST_OF]: {
        gameType: R.gameTypeBestOf5,
        gameTypeInfo: R.gameTypeBestOf5Info
    },
    [GAMETYPE_TIME]: {
        gameType: R.gameTypeTime,
        gameTypeInfo: R.gameTypeTimeInfo
    },
    [GAMETYPE_BOMB]: {
        gameType: R.gameTypeBomb,
        gameTypeInfo: R.gameTypeBombInfo
    }
};

// Constants
const FINISHED_TEXTS = {
    win: {
        title: R.youWin,
        text: R.gameWinText
    },
    lost: {
        title: R.youLost,
        text: R.gameLostText
    }
};

// Event delegate component
class DelegateComponent extends Component {
    constructor(handler) {
        super();
        this.handler = handler;
    }

    handleEvent(e) {
        this.handler.handleEvent(e, this.state);
    }
}

// Predefined

export default {
    props: ["params"],
    data() {
        return { backgroundStyle: null, state: null, introTexts: {}, finishTexts: {}, player1: "", player2: "" };
    },
    mounted() {
        // Create ui connector
        this.uiConnector = new DelegateComponent({
            handleEvent: (e, state) => {
                if (e.type == EVENT_ENGINE_POST_START) {
                    this.state = "intro";
                    this.introTexts = INTRO_TEXTS[state.type];
                } else if (e.type == EVENT_INGAME_FINISHED) {
                    this.state = "finished";
                    this.finishTexts = FINISHED_TEXTS[PLAYER_1 == state.won ? "win" : "lost"];

                    // Add game result
                    this.$store.dispatch("stats/addGameResult", {
                        date: new Date().getTime(),
                        win: PLAYER_1 == state.won,
                        level: state.levelResource,
                        gameType: state.type,
                        difficulty: state.difficulty,
                        score: {
                            player1: state.player1.score,
                            player2: state.player2.score
                        }
                    });
                } else if (e.type == EVENT_INGAME_KICKOFF || e.type == EVENT_GAME_RESUME) {
                    this.state = "running";
                } else if (e.type == EVENT_GAME_PAUSE) {
                    this.state = "paused";
                }
            }
        });

        // Init engine
        this.init();

        // FIXME DEV
        document.addEventListener("keydown", e => {
            if (e.key == "R") {
                this.reset();
            } else if (e.key == "Q") {
                this.quit();
            }
        });
    },
    beforeDestroy() {
        // Dispose engine
        this.engine.dispose();
    },
    methods: {
        async init() {
            // Create game object
            const game = {
                canvas: "game",
                components: [this.uiConnector, new GameLogic(), new GameInput(), new GamePhysics(), new GameRenderer(), new GameRendererHUD(), new GameVibrator({ enabled: false }), new GameSound({ enabled: this.$store.state.settings.sound, howlerOptions: { html5: true } })],
                state: new GameState()
            };

            // Init game
            game.state.difficulty = this.$store.state.settings.difficulty;
            game.state.levelResource = this.params.l || randa(this.$store.state.res.levels);
            game.state.player1Resource = this.params.p || randa(this.$store.state.res.players);
            game.state.type = this.params.t || 1 + Math.round(Math.random() * 2);
            do {
                game.state.player2Resource = randa(this.$store.state.res.players);
            } while (game.state.player2Resource == game.state.player1Resource);

            // Init data
            this.player1 = game.state.player1Resource;
            this.player2 = game.state.player2Resource;

            // Start game
            this.engine = new Engine();
            await this.engine.start(game);
        },
        start() {
            this.engine.fireEvent(EVENT_INGAME_KICKOFF, 100);
        },
        reset() {
            this.engine.fireEvent(EVENT_GAME_RESUME);
            this.engine.fireEvent(EVENT_GAME_RESET);
            this.engine.fireEvent(EVENT_ENGINE_POST_START);
        },
        pause() {
            this.engine.fireEvent(EVENT_GAME_PAUSE);
        },
        resume() {
            this.engine.fireEvent(EVENT_GAME_RESUME);
        },
        quit() {
            this.$emit("quit");
        }
    }
};
</script>